import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  CircleAlert,
  Crosshair,
  Eye,
  EyeOff,
  ListFilter,
  Map as MapIcon,
  MapPin,
  MapPinOff,
  Radar,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import { fetchAdminNetworkMap, type AdminNetworkMapLead } from "@/api/adminNetworkMap";
import { AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";
import { NetworkPartnerList } from "@/components/admin/NetworkPartnerList";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/StatCard";
import { usePartners, type Partner } from "@/context/PartnerContext";
import type { ServiceType } from "@/context/LeadContext";
import { SERVICE_OPTIONS as CATALOG_SERVICE_OPTIONS } from "@/lib/serviceCatalog";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";

type StatusFilter = "active" | "inactive" | "all";
type ReadinessFilter = "all" | "ready" | "attention" | "no_coordinates";
type MapStatus = "idle" | "loading" | "ready" | "error";

const GREECE_CENTER = { lat: 38.7, lng: 24 };
const formatNumber = new Intl.NumberFormat("el-GR").format;

const SERVICE_OPTIONS: Array<{ value: ServiceType | "all"; label: string }> = [
  { value: "all", label: "Όλες οι υπηρεσίες" },
  ...CATALOG_SERVICE_OPTIONS.map(({ value, label }) => ({ value, label })),
];

const hasCoordinates = (partner: Partner): partner is Partner & { latitude: number; longitude: number } =>
  typeof partner.latitude === "number" && Number.isFinite(partner.latitude)
  && typeof partner.longitude === "number" && Number.isFinite(partner.longitude);

const serviceLabel = (service: ServiceType): string =>
  SERVICE_OPTIONS.find((option) => option.value === service)?.label || service;

const createTextElement = (tag: keyof HTMLElementTagNameMap, className: string, value: string): HTMLElement => {
  const element = document.createElement(tag);
  element.className = className;
  element.textContent = value;
  return element;
};

const createPartnerInfoContent = (partner: Partner): HTMLElement => {
  const root = document.createElement("div");
  root.className = "min-w-[220px] max-w-[280px] p-2 font-sans text-slate-800";
  root.appendChild(createTextElement("p", "text-sm font-bold text-slate-950", partner.name));
  root.appendChild(createTextElement("p", "mt-1 text-xs leading-5 text-slate-500", partner.location || "Χωρίς καταχωρημένη περιοχή"));

  const metrics = document.createElement("div");
  metrics.className = "mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center";
  metrics.appendChild(createTextElement("div", "rounded-lg bg-slate-50 px-2 py-2 text-xs font-semibold", `${partner.radius || 0} km`));
  metrics.appendChild(createTextElement("div", "rounded-lg bg-sky-50 px-2 py-2 text-xs font-semibold text-sky-800", `${partner.leads30 || 0} · 30ημ.`));
  metrics.appendChild(createTextElement("div", "rounded-lg bg-emerald-50 px-2 py-2 text-xs font-semibold text-emerald-800", partner.routingReady ? "Έτοιμος" : "Έλεγχος"));
  root.appendChild(metrics);

  const link = document.createElement("a");
  link.className = "mt-3 block rounded-lg bg-slate-950 px-3 py-2 text-center text-xs font-semibold text-white";
  link.href = `/admin/partners/${encodeURIComponent(partner.id)}/dashboard`;
  link.textContent = "Άνοιγμα συνεργάτη";
  root.appendChild(link);

  return root;
};

const createLeadInfoContent = (lead: AdminNetworkMapLead): HTMLElement => {
  const root = document.createElement("div");
  root.className = "min-w-[200px] max-w-[260px] p-2 font-sans text-slate-800";
  root.appendChild(createTextElement("p", "text-xs font-bold uppercase tracking-wide text-amber-700", "Χωρίς συνεργάτη"));
  root.appendChild(createTextElement("p", "mt-2 text-sm font-semibold text-slate-950", serviceLabel(lead.service)));
  root.appendChild(createTextElement("p", "mt-1 text-xs text-slate-500", lead.postalCode ? `Τ.Κ. ${lead.postalCode}` : "Χωρίς ταχυδρομικό κώδικα"));

  const link = document.createElement("a");
  link.className = "mt-3 block rounded-lg bg-amber-600 px-3 py-2 text-center text-xs font-semibold text-white";
  link.href = `/admin/lead/${encodeURIComponent(lead.id)}`;
  link.textContent = "Άνοιγμα στο Operations";
  root.appendChild(link);

  return root;
};

const PartnersMap: React.FC = () => {
  const {
    partners,
    loading: partnersLoading,
    error: partnersError,
    directorySummary,
    refreshPartners,
  } = usePartners();
  const networkQuery = useQuery({
    queryKey: ["admin-network-map"],
    queryFn: fetchAdminNetworkMap,
    staleTime: 60_000,
  });

  const mapElementRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const partnerMarkersRef = useRef<Record<string, google.maps.Marker>>({});
  const leadMarkersRef = useRef<Record<string, google.maps.Marker>>({});
  const circlesRef = useRef<Record<string, google.maps.Circle>>({});
  const partnerInfoWindowsRef = useRef<Record<string, google.maps.InfoWindow>>({});
  const leadInfoWindowsRef = useRef<Record<string, google.maps.InfoWindow>>({});

  const [mapStatus, setMapStatus] = useState<MapStatus>("idle");
  const [mapError, setMapError] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim().toLocaleLowerCase("el-GR"));
  const [status, setStatus] = useState<StatusFilter>("active");
  const [service, setService] = useState<ServiceType | "all">("all");
  const [readiness, setReadiness] = useState<ReadinessFilter>("all");
  const [showCoverage, setShowCoverage] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const filteredPartners = useMemo(() => partners.filter((partner) => {
    if (status === "active" && !partner.active) return false;
    if (status === "inactive" && partner.active) return false;
    if (service !== "all" && !partner.services.includes(service)) return false;
    if (readiness === "ready" && !partner.routingReady) return false;
    if (readiness === "attention" && (!partner.active || (partner.attentionReasons?.length || 0) === 0)) return false;
    if (readiness === "no_coordinates" && hasCoordinates(partner)) return false;
    if (!deferredSearch) return true;
    return [partner.name, partner.location].some((value) => value.toLocaleLowerCase("el-GR").includes(deferredSearch));
  }), [deferredSearch, partners, readiness, service, status]);

  const visibleUnassignedLeads = useMemo(() => {
    const leads = networkQuery.data?.unassignedLeads || [];
    return service === "all" ? leads : leads.filter((lead) => lead.service === service);
  }, [networkQuery.data?.unassignedLeads, service]);

  const selectedPartner = selectedPartnerId
    ? partners.find((partner) => partner.id === selectedPartnerId) || null
    : null;
  const mappedPartnerCount = filteredPartners.filter(hasCoordinates).length;
  const missingPartnerCoordinates = Math.max(0, directorySummary.active - directorySummary.geocoded);
  const unassignedSummary = networkQuery.data?.summary;
  const hasFilters = search !== "" || status !== "active" || service !== "all" || readiness !== "all";
  const activeAdvancedFilters = Number(status !== "active") + Number(service !== "all") + Number(readiness !== "all");

  const closeInfoWindows = useCallback(() => {
    Object.values(partnerInfoWindowsRef.current).forEach((window) => window.close());
    Object.values(leadInfoWindowsRef.current).forEach((window) => window.close());
  }, []);

  const clearOverlays = useCallback(() => {
    closeInfoWindows();
    Object.values(partnerMarkersRef.current).forEach((marker) => {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    });
    Object.values(leadMarkersRef.current).forEach((marker) => {
      google.maps.event.clearInstanceListeners(marker);
      marker.setMap(null);
    });
    Object.values(circlesRef.current).forEach((circle) => circle.setMap(null));
    partnerMarkersRef.current = {};
    leadMarkersRef.current = {};
    circlesRef.current = {};
    partnerInfoWindowsRef.current = {};
    leadInfoWindowsRef.current = {};
  }, [closeInfoWindows]);

  const fitVisibleMarkers = useCallback(() => {
    const map = googleMapRef.current;
    if (!map || !window.google?.maps) return;
    const markers = [...Object.values(partnerMarkersRef.current), ...Object.values(leadMarkersRef.current)];
    const positions = markers.map((marker) => marker.getPosition()).filter((position): position is google.maps.LatLng => Boolean(position));

    if (positions.length === 0) {
      map.setCenter(GREECE_CENTER);
      map.setZoom(7);
      return;
    }
    if (positions.length === 1) {
      map.panTo(positions[0]);
      map.setZoom(10);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    positions.forEach((position) => bounds.extend(position));
    map.fitBounds(bounds, { top: 56, right: 56, bottom: 56, left: 56 });
  }, []);

  const renderOverlays = useCallback(() => {
    const map = googleMapRef.current;
    if (!map || !window.google?.maps) return;
    clearOverlays();

    filteredPartners.forEach((partner) => {
      if (!hasCoordinates(partner)) return;
      const position = { lat: partner.latitude, lng: partner.longitude };
      const ready = partner.active && Boolean(partner.routingReady);
      const pinColor = !partner.active ? "#64748b" : ready ? "#0369a1" : "#d97706";
      const marker = new google.maps.Marker({
        position,
        map,
        title: partner.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: pinColor,
          fillOpacity: 0.96,
          strokeWeight: 3,
          strokeColor: "#ffffff",
          scale: 9,
        },
        zIndex: partner.active ? 10 : 5,
      });

      const infoWindow = new google.maps.InfoWindow({
        content: createPartnerInfoContent(partner),
        pixelOffset: new google.maps.Size(0, -10),
      });
      partnerMarkersRef.current[partner.id] = marker;
      partnerInfoWindowsRef.current[partner.id] = infoWindow;

      if (showCoverage && partner.active && (partner.radius || 0) > 0) {
        circlesRef.current[partner.id] = new google.maps.Circle({
          strokeColor: pinColor,
          strokeOpacity: ready ? 0.45 : 0.6,
          strokeWeight: 1,
          fillColor: pinColor,
          fillOpacity: ready ? 0.08 : 0.12,
          map,
          center: position,
          radius: (partner.radius || 0) * 1000,
          zIndex: 1,
        });
      }

      google.maps.event.addListener(marker, "click", () => {
        closeInfoWindows();
        infoWindow.open(map, marker);
        setSelectedPartnerId(partner.id);
      });
    });

    if (showUnassigned) {
      visibleUnassignedLeads.forEach((lead) => {
        if (!Number.isFinite(lead.latitude) || !Number.isFinite(lead.longitude)) return;
        const marker = new google.maps.Marker({
          position: { lat: lead.latitude, lng: lead.longitude },
          map,
          title: `Χωρίς συνεργάτη · ${serviceLabel(lead.service)}`,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: "#e11d48",
            fillOpacity: 0.96,
            strokeWeight: 3,
            strokeColor: "#ffffff",
            scale: 7,
          },
          zIndex: 20,
        });
        const infoWindow = new google.maps.InfoWindow({
          content: createLeadInfoContent(lead),
          pixelOffset: new google.maps.Size(0, -8),
        });
        leadMarkersRef.current[lead.id] = marker;
        leadInfoWindowsRef.current[lead.id] = infoWindow;
        google.maps.event.addListener(marker, "click", () => {
          closeInfoWindows();
          setSelectedPartnerId(null);
          infoWindow.open(map, marker);
        });
      });
    }

    fitVisibleMarkers();
  }, [clearOverlays, closeInfoWindows, filteredPartners, fitVisibleMarkers, showCoverage, showUnassigned, visibleUnassignedLeads]);

  const initializeMap = useCallback(async () => {
    setMapStatus("loading");
    setMapError("");
    try {
      await loadGoogleMapsScript();
      if (!mapElementRef.current || !window.google?.maps) throw new Error("map_canvas_unavailable");
      if (!googleMapRef.current) {
        googleMapRef.current = new window.google.maps.Map(mapElementRef.current, {
          center: GREECE_CENTER,
          zoom: 7,
          styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
          mapTypeControl: false,
          fullscreenControl: true,
          streetViewControl: false,
          zoomControl: true,
          zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
        });
      }
      setMapStatus("ready");
    } catch (error) {
      console.error("Failed to load Google Maps:", error);
      setMapError("Ο χάρτης Google δεν φορτώθηκε. Η λίστα συνεργατών παραμένει διαθέσιμη.");
      setMapStatus("error");
    }
  }, []);

  useEffect(() => {
    void initializeMap();
    return () => clearOverlays();
  }, [clearOverlays, initializeMap]);

  useEffect(() => {
    if (mapStatus === "ready") renderOverlays();
  }, [mapStatus, renderOverlays]);

  useEffect(() => {
    if (selectedPartnerId && !filteredPartners.some((partner) => partner.id === selectedPartnerId)) {
      setSelectedPartnerId(null);
      closeInfoWindows();
    }
  }, [closeInfoWindows, filteredPartners, selectedPartnerId]);

  const zoomToPartner = (partnerId: string) => {
    const map = googleMapRef.current;
    const marker = partnerMarkersRef.current[partnerId];
    const infoWindow = partnerInfoWindowsRef.current[partnerId];
    if (!map || !marker || !infoWindow) return;
    const position = marker.getPosition();
    if (!position) return;
    closeInfoWindows();
    map.panTo(position);
    map.setZoom(11);
    infoWindow.open(map, marker);
    setSelectedPartnerId(partnerId);
  };

  const clearSelection = () => {
    setSelectedPartnerId(null);
    closeInfoWindows();
    fitVisibleMarkers();
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("active");
    setService("all");
    setReadiness("all");
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refreshPartners(), networkQuery.refetch()]);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (partnersLoading && partners.length === 0) {
    return <div className="space-y-6"><AdminMetricSkeletons count={4} /><Skeleton className="h-[720px] rounded-2xl" /></div>;
  }

  if (partnersError && partners.length === 0) {
    return <AdminErrorState message={partnersError} onRetry={() => void refreshPartners()} />;
  }

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        eyebrow="Δίκτυο συνεργατών"
        title="Χάρτης κάλυψης"
        description="Δείτε πού υπάρχει πραγματική κάλυψη, ποιοι συνεργάτες είναι διαθέσιμοι και ποια leads περιμένουν χειροκίνητη διαχείριση."
        icon={<MapIcon />}
        iconGradient="blue"
        actions={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
            <Button asChild variant="outline" className="h-11 gap-2"><Link to="/admin/partners"><ArrowLeft className="h-4 w-4" /> Συνεργάτες</Link></Button>
            <Button asChild className="h-11 gap-2 bg-slate-950 text-white hover:bg-slate-800"><Link to="/admin/operations"><AlertTriangle className="h-4 w-4" /> Operations</Link></Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard title="Ενεργά σημεία" value={formatNumber(directorySummary.geocoded)} description={`${formatNumber(directorySummary.active)} ενεργοί συνεργάτες`} icon={<MapPin />} variant="info" size="compact" />
        <StatCard title="Έτοιμοι για routing" value={formatNumber(directorySummary.routing_ready)} description={`${missingPartnerCoordinates} ενεργοί χωρίς θέση`} icon={<Radar />} variant={directorySummary.needs_attention > 0 ? "warning" : "success"} size="compact" />
        <StatCard title="Χωρίς συνεργάτη" value={formatNumber(unassignedSummary?.unassignedTotal || 0)} description={`${formatNumber(unassignedSummary?.unassignedMappable || 0)} εμφανίζονται στον χάρτη`} icon={<CircleAlert />} variant={(unassignedSummary?.unassignedTotal || 0) > 0 ? "danger" : "success"} size="compact" />
        <StatCard title="Χωρίς συντεταγμένες" value={formatNumber(missingPartnerCoordinates + (unassignedSummary?.unassignedWithoutCoordinates || 0))} description="Συνεργάτες και unassigned leads" icon={<MapPinOff />} variant={missingPartnerCoordinates + (unassignedSummary?.unassignedWithoutCoordinates || 0) > 0 ? "warning" : "success"} size="compact" />
      </div>

      {networkQuery.isError && (
        <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between">
          <div><p className="font-semibold">Δεν φορτώθηκαν τα unassigned σημεία</p><p className="mt-1 text-xs leading-5 text-amber-800">Οι συνεργάτες παραμένουν διαθέσιμοι στον χάρτη. Δοκιμάστε ξανά για την πλήρη operational εικόνα.</p></div>
          <Button type="button" variant="outline" className="h-11 border-amber-300 bg-white" onClick={() => void networkQuery.refetch()}>Νέα προσπάθεια</Button>
        </div>
      )}

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardHeader className="p-4 pb-0 sm:p-5 sm:pb-0">
          <div className="flex items-start justify-between gap-3">
            <div><CardTitle className="flex items-center gap-2 text-base text-slate-950"><ListFilter className="h-4 w-4 text-sky-700" /> Έλεγχος προβολής</CardTitle><p className="mt-1 text-xs text-slate-500">Τα φίλτρα ενημερώνουν ταυτόχρονα λίστα, pins και εμβέλειες.</p></div>
            {hasFilters && <Button type="button" variant="ghost" aria-label="Καθαρισμός φίλτρων" className="h-11 shrink-0 gap-1.5 text-slate-600" onClick={resetFilters}><X className="h-4 w-4" /><span className="hidden sm:inline">Καθαρισμός</span></Button>}
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5 xl:grid-cols-4 2xl:grid-cols-[minmax(240px,1fr)_170px_220px_210px_auto_auto_auto]">
          <div className="relative sm:col-span-2 2xl:col-span-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Όνομα ή περιοχή" aria-label="Αναζήτηση στον χάρτη" className="h-11 pl-9 pr-11" />
            {search && <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-11 w-11" onClick={() => setSearch("")} aria-label="Καθαρισμός αναζήτησης"><X className="h-4 w-4" /></Button>}
          </div>
          <Button type="button" variant="outline" className="h-11 justify-between lg:hidden" aria-label="Προχωρημένα φίλτρα" aria-expanded={mobileFiltersOpen} onClick={() => setMobileFiltersOpen((value) => !value)}><span className="flex items-center gap-2"><ListFilter className="h-4 w-4" /> Προχωρημένα φίλτρα</span>{activeAdvancedFilters > 0 && <Badge className="bg-sky-700 text-white">{activeAdvancedFilters}</Badge>}</Button>
          <div className={`${mobileFiltersOpen ? "grid" : "hidden"} col-span-full gap-3 sm:grid-cols-3 lg:contents`}>
            <Select value={status} onValueChange={(value) => setStatus(value as StatusFilter)}><SelectTrigger className="h-11" aria-label="Κατάσταση συνεργάτη"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Ενεργοί</SelectItem><SelectItem value="inactive">Ανενεργοί</SelectItem><SelectItem value="all">Όλοι</SelectItem></SelectContent></Select>
            <Select value={service} onValueChange={(value) => setService(value as ServiceType | "all")}><SelectTrigger className="h-11" aria-label="Υπηρεσία χάρτη"><SelectValue /></SelectTrigger><SelectContent>{SERVICE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select>
            <Select value={readiness} onValueChange={(value) => setReadiness(value as ReadinessFilter)}><SelectTrigger className="h-11" aria-label="Ετοιμότητα χάρτη"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Κάθε ετοιμότητα</SelectItem><SelectItem value="ready">Έτοιμοι για routing</SelectItem><SelectItem value="attention">Χρειάζονται έλεγχο</SelectItem><SelectItem value="no_coordinates">Χωρίς συντεταγμένες</SelectItem></SelectContent></Select>
          </div>
          <div className="col-span-full grid grid-cols-[1fr_1fr_44px] gap-2 lg:contents">
            <Button type="button" variant="outline" className={`h-11 gap-1.5 px-2 text-xs sm:text-sm ${showCoverage ? "border-sky-300 bg-sky-50 text-sky-800" : "text-slate-600"}`} aria-pressed={showCoverage} onClick={() => setShowCoverage((value) => !value)}>{showCoverage ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} Εμβέλειες</Button>
            <Button type="button" variant="outline" className={`h-11 gap-1.5 px-2 text-xs sm:text-sm ${showUnassigned ? "border-rose-300 bg-rose-50 text-rose-800" : "text-slate-600"}`} aria-pressed={showUnassigned} onClick={() => setShowUnassigned((value) => !value)}>{showUnassigned ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} Unassigned</Button>
            <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={fitVisibleMarkers} aria-label="Προσαρμογή χάρτη στα ορατά σημεία"><Crosshair className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid min-w-0 gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        <div className="order-2 min-w-0 lg:order-1">
          <NetworkPartnerList partners={filteredPartners} selectedPartnerId={selectedPartnerId} onSelect={zoomToPartner} onResetFilters={resetFilters} />
        </div>

        <Card className="order-1 min-w-0 overflow-hidden rounded-2xl border-slate-200 shadow-sm lg:order-2 lg:h-[720px]">
          <CardHeader className="border-b border-slate-100 bg-white p-4 sm:p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div><CardTitle className="text-base text-slate-950">Πραγματική γεωγραφική κάλυψη</CardTitle><p className="mt-1 text-xs text-slate-500">{formatNumber(mappedPartnerCount)} partner pins · {showUnassigned ? formatNumber(visibleUnassignedLeads.length) : 0} unassigned pins</p></div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-medium text-slate-600">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-sky-700 ring-2 ring-white" /> Έτοιμος</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-600 ring-2 ring-white" /> Έλεγχος</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-slate-500 ring-2 ring-white" /> Ανενεργός</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-600 ring-2 ring-white" /> Unassigned</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative h-[520px] p-0 sm:h-[600px] lg:h-[calc(720px-93px)]">
            <div ref={mapElementRef} className="h-full w-full bg-slate-100" aria-label="Χάρτης γεωγραφικής κάλυψης συνεργατών" />

            {(mapStatus === "idle" || mapStatus === "loading") && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-50/90 backdrop-blur-sm">
                <div className="text-center"><RefreshCw className="mx-auto h-8 w-8 animate-spin text-sky-700" /><p className="mt-3 font-semibold text-slate-800">Φόρτωση χάρτη…</p><p className="mt-1 text-xs text-slate-500">Προετοιμασία pins και εμβελειών</p></div>
              </div>
            )}

            {mapStatus === "error" && (
              <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-50 p-6">
                <div className="max-w-sm text-center"><MapPinOff className="mx-auto h-9 w-9 text-amber-600" /><p className="mt-4 font-semibold text-slate-900">Ο χάρτης δεν είναι διαθέσιμος</p><p className="mt-2 text-sm leading-6 text-slate-600">{mapError}</p><Button type="button" className="mt-4 h-11 gap-2" onClick={() => void initializeMap()}><RefreshCw className="h-4 w-4" /> Νέα προσπάθεια</Button></div>
              </div>
            )}

            {selectedPartner && (
              <div className="absolute inset-x-3 bottom-3 z-10 rounded-2xl border border-white/70 bg-white/95 p-3 shadow-xl backdrop-blur sm:inset-x-auto sm:bottom-4 sm:left-4 sm:w-[340px]">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-950 text-white"><MapPin className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-slate-950">{selectedPartner.name}</p><p className="mt-1 truncate text-xs text-slate-500">{selectedPartner.location || "Χωρίς περιοχή"}</p></div>
                  <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" onClick={clearSelection} aria-label="Κλείσιμο επιλογής"><X className="h-4 w-4" /></Button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs"><div className="rounded-lg bg-slate-50 p-2"><p className="font-bold text-slate-900">{selectedPartner.radius || 0} km</p><p className="text-[10px] text-slate-400">Ακτίνα</p></div><div className="rounded-lg bg-sky-50 p-2"><p className="font-bold text-sky-800">{selectedPartner.leads30 || 0}</p><p className="text-[10px] text-sky-600">30 ημέρες</p></div><div className="rounded-lg bg-emerald-50 p-2"><p className="font-bold text-emerald-800">{selectedPartner.routingReady ? "Έτοιμος" : "Έλεγχος"}</p><p className="text-[10px] text-emerald-600">Routing</p></div></div>
                <Button asChild className="mt-3 h-11 w-full bg-slate-950 text-white hover:bg-slate-800"><Link to={`/admin/partners/${selectedPartner.id}/dashboard`}>Άνοιγμα συνεργάτη</Link></Button>
              </div>
            )}

            <Button type="button" variant="outline" size="icon" className="absolute bottom-4 right-4 z-10 h-11 w-11 bg-white/95 shadow-lg backdrop-blur" onClick={fitVisibleMarkers} aria-label="Εμφάνιση όλων των ορατών σημείων"><Crosshair className="h-4 w-4" /></Button>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600 sm:flex-row sm:items-center sm:justify-between">
        <p><strong className="text-slate-800">Σημείωση:</strong> οι κύκλοι δείχνουν τη δηλωμένη ακτίνα κάθε συνεργάτη· δεν παρακάμπτουν τον κανόνα υπηρεσίας, απόστασης και φόρτου της αυτόματης ανάθεσης.</p>
        <div className="flex items-center gap-2 text-slate-500"><Users className="h-4 w-4" /><span>{formatNumber(directorySummary.active)} ενεργοί</span>{isRefreshing && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}<Button type="button" variant="ghost" className="h-11 px-3" disabled={isRefreshing} onClick={() => void handleRefresh()}>{isRefreshing ? "Ανανέωση…" : "Ανανέωση δεδομένων"}</Button></div>
      </div>
    </div>
  );
};

export default PartnersMap;
